/******************************************************************************
 mlib-components
 Library for UI components widely used in LB apps.
 (c) Longboard Architectural Products
 ******************************************************************************/
import { __awaiter, __generator } from '../../_virtual/_tslib.js';
import axios from 'axios';
import React__default, { useContext, useCallback, useLayoutEffect } from 'react';
import AuthContext from '../../contexts/auth-context/auth-context.js';
import { refreshToken } from '../../utils/TokenHandler/token-handler.js';
import { jwtDecode } from '../../node_modules/jwt-decode/build/esm/index.js';
import toast from '../SnackBar/toast.js';
import mem from '../../node_modules/mem/dist/index.js';
import AlertDialog from '../AlertDialog/alert-dialog.js';
import RunConfig from '../../utils/RunConfig/runconfig.js';

var apiUrl = RunConfig === null || RunConfig === void 0 ? void 0 : RunConfig.apiUrl;
var AxiosInterceptor = function (_a) {
    var children = _a.children;
    var authCtx = useContext(AuthContext);
    var funMsg = 'No response on the other end. Try again after cup of coffee.';
    var refresh = useCallback(function () {
        return refreshToken(authCtx.refreshToken || '')
            .then(function (respToken) {
            var accessTokenDecoded = jwtDecode(respToken.access_token);
            authCtx.update(accessTokenDecoded.name, respToken.access_token, accessTokenDecoded.exp, respToken.refresh_token, respToken.refresh_token_exp, accessTokenDecoded.azp, {
                realm_access: accessTokenDecoded.realm_access,
                resource_access: accessTokenDecoded.resource_access
            });
            return Promise.resolve(respToken);
        });
    }, [authCtx]);
    var memoizedRefreshToken = mem(refresh, { "maxAge": 20000 });
    var onRequest = useCallback(function (config) { return __awaiter(void 0, void 0, void 0, function () {
        var token;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!config.headers["Authorization"]) {
                        config.headers["Authorization"] = "Bearer ".concat(authCtx.accessToken);
                    }
                    if (!!authCtx.isLoggedIn()) return [3 /*break*/, 1];
                    throw new axios.Cancel('Not logged in');
                case 1:
                    if (!authCtx.isAccessTokenExpired()) return [3 /*break*/, 3];
                    return [4 /*yield*/, memoizedRefreshToken().catch(function () {
                            throw new axios.Cancel('Could not refresh token');
                        })];
                case 2:
                    token = _a.sent();
                    config.headers["Authorization"] = "Bearer ".concat(token.access_token);
                    _a.label = 3;
                case 3: return [2 /*return*/, config];
            }
        });
    }); }, [authCtx, memoizedRefreshToken]);
    var onRequestError = useCallback(function (error) {
        if (axios.isCancel(error)) {
            authCtx.clear();
        }
        return Promise.reject(error);
    }, [authCtx]);
    var onResponse = useCallback(function (response) {
        return response;
    }, []);
    var onResponseError = useCallback(function (error) {
        var _a, _b, _c, _d, _e;
        if (axios.isCancel(error)) {
            authCtx.clear();
            return Promise.reject(error);
        }
        var originalRequest = error.config;
        // retry request if refresh succeeds, logout otherwise
        if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 401) {
            return memoizedRefreshToken().then(function (token) {
                originalRequest.headers['Authorization'] = "Bearer ".concat(token.access_token);
                originalRequest.baseURL = undefined;
                return axios.request(originalRequest);
            }).catch(function () {
                authCtx.clear();
                return Promise.reject(error);
            });
        }
        // Pop up error notification if any
        var errorMsg = originalRequest.headers['Error-Message'];
        if (errorMsg) {
            var alertMsg_1 = (_d = (_c = (_b = error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.detail) !== null && _d !== void 0 ? _d : (error.message === 'Network Error' ? funMsg : error.message);
            toast.error(((_e = error.response) === null || _e === void 0 ? void 0 : _e.status) === 403 ? 'Access denied.' : errorMsg, {
                action: function (_a) {
                    var key = _a.key;
                    return (React__default.createElement(AlertDialog, { message: alertMsg_1, key: key }));
                },
            });
        }
        return Promise.reject(error);
    }, [authCtx, memoizedRefreshToken]);
    useLayoutEffect(function () {
        axios.defaults.baseURL = apiUrl;
        axios.defaults.headers.common['Content-Type'] = 'application/json';
        axios.defaults.headers.common['Error-Message'] = 'There was an error processing your request';
        // axios.defaults.headers.common['Authorization'] = `Bearer ${authCtx.accessToken}`
    }, [authCtx.accessToken]);
    useLayoutEffect(function () {
        var requestInterceptor = axios.interceptors.request.use(onRequest, onRequestError);
        var responseInterceptor = axios.interceptors.response.use(onResponse, onResponseError);
        return function () {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, [onRequest, onRequestError, onResponse, onResponseError]);
    return React__default.createElement(React__default.Fragment, null, children);
};
var AxiosInterceptor$1 = AxiosInterceptor;

export { AxiosInterceptor$1 as default };
